"use client";

import { useEffect, useState } from "react";

type MediaQueryChangeListener = (this: MediaQueryList, ev: MediaQueryListEvent) => void;

type MediaQueryListExtended = MediaQueryList & {
  addEventListener?: (type: "change", listener: MediaQueryChangeListener) => void;
  removeEventListener?: (type: "change", listener: MediaQueryChangeListener) => void;
  addListener?: (listener: MediaQueryChangeListener) => void;
  removeListener?: (listener: MediaQueryChangeListener) => void;
};

export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return false;
    }
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return;
    }

    const mql = window.matchMedia(query) as MediaQueryListExtended;
    setMatches(mql.matches);

    const listener: MediaQueryChangeListener = (e) => setMatches(e.matches);

    if (typeof mql.addEventListener === "function") {
      mql.addEventListener("change", listener);
    } else if (typeof mql.addListener === "function") {
      mql.addListener(listener);
    }

    return () => {
      if (typeof mql.removeEventListener === "function") {
        mql.removeEventListener("change", listener);
      } else if (typeof mql.removeListener === "function") {
        mql.removeListener(listener);
      }
    };
  }, [query]);

  return matches;
}
